import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import PropTypes from "prop-types";
import { useNavigate } from 'react-router-dom';

import cameraOnline from 'assets/images/camera_online.png';
import cameraWarning from 'assets/images/camera_warning.png';
import cameraOffline from 'assets/images/camera_offline.png';
import deviceSleeping from 'assets/images/device_sleeping.png';
import deviceEcoMode from 'assets/images/eco_mode.png';
import deviceEcoModeWarning from 'assets/images/eco_mode_low.png';

import { setGroupFeatures, setDeviceFeatures } from 'utils/map';
import { useSoftUIController } from 'hooks/context';

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

import { useEffect, useRef } from 'react';

// 1. Added isEditMode and handleLocationChange props
function StopmonitorMap({ map, user, devices, groups, attentionDevices, setSelectedDevice, mapOption, setMapOption, isAttentionMode, isEditMode, handleLocationChange }) {
    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { selectedOrg, userPermissions } = controller;
    const showAdvancedDeviceLevel = userPermissions.indexOf('devices-advanced:view') >= 0;
    const isClearways = userPermissions.indexOf('clearways') >= 0;

    const popupRef = useRef(new mapboxgl.Popup({ closeButton: false, closeOnClick: false }));

    // 2. Ref to hold the temporary draggable markers during edit mode
    const editMarkersRef = useRef([]);

    const handleLocationChangeRef = useRef(handleLocationChange);
    useEffect(() => {
        handleLocationChangeRef.current = handleLocationChange;
    }, [handleLocationChange]);

    const stateRef = useRef({
        devices,
        groups,
        mapOption,
        showAdvancedDeviceLevel,
        isClearways,
        uniqueZIndices: []
    });

    // Keep stateRef up to date with incoming props instantly
    useEffect(() => {
        let remainingAttentionDevices = isAttentionMode && attentionDevices ? [...attentionDevices] : [];

        const allDevices = devices.map(dev => {
            let isMatchedAttentionDevice = false;

            if (isAttentionMode && remainingAttentionDevices.length > 0) {
                let foundIndex = remainingAttentionDevices.findIndex(d => d.stop_id === dev.site_id);

                if (foundIndex !== -1) {
                    isMatchedAttentionDevice = true;
                    let found = remainingAttentionDevices[foundIndex];
                    dev = {
                        ...dev,
                        title: found.title,
                        status: found.status,
                        inspection_record_id: found.inspection_record_id,
                        description: found.description,
                        images: found.images,
                        inspectionCreatedAt: found.inspectionCreatedAt
                    }

                    remainingAttentionDevices.splice(foundIndex, 1);
                }
            }

            return setDeviceFeatures(dev, showAdvancedDeviceLevel, mapOption, isAttentionMode);
        }).concat(remainingAttentionDevices.map(d => setDeviceFeatures(d, showAdvancedDeviceLevel, mapOption, isAttentionMode))).sort((a, b) => a.properties.zIndex - b.properties.zIndex);

        const uniqueZIndices = [...new Set(allDevices.map(f => f.properties.zIndex).filter(z => z !== undefined))].sort((a, b) => a - b);

        stateRef.current = {
            devices: allDevices,
            groups,
            mapOption,
            showAdvancedDeviceLevel,
            isClearways,
            uniqueZIndices
        };

        const mapInstance = map.current;
        if (mapInstance && mapInstance.isStyleLoaded()) {
            updateMapData(mapInstance);
        }
    }, [devices, groups, mapOption, showAdvancedDeviceLevel, isClearways, isAttentionMode, attentionDevices]);

    // --- Dynamic Handlers ---
    const handleDeviceClick = (e) => {
        if (isEditMode) return; // Prevent navigation clicks if we are in edit mode
        e.preventDefault();
        popupRef.current.remove();
        var path = "";
        if (isAttentionMode) { // ADJUST ASSIGNMENTS
            path = "/operations";
        }
        else {
            path = "/devices/" + e.features[0].properties.uuid;
        }
        const browserEvent = e.originalEvent;
        if (browserEvent && (browserEvent.ctrlKey || browserEvent.metaKey)) {
            window.open(path, '_blank');
        } else {
            navigate(path);
        }
    };

    const handleMapClick = (e) => {
        if (isEditMode) return;
        if (!e.defaultPrevented) {
            setMapOption(null);
            setSelectedDevice(null);
        }
    };

    const handleGroupClick = (e) => {
        if (isEditMode) return;
        e.preventDefault();
        if (!stateRef.current.isClearways && isNaN(e.features[0].properties.group_id)) {
            navigate("/kerbsides/" + e.features[0].properties.group_id);
        }
    };

    const handleMouseEnter = (e) => {
        if (!map.current || isEditMode || (isAttentionMode && typeof e.features[0].properties?.inspection_record_id == 'undefined')) return;

        map.current.getCanvas().style.cursor = 'pointer';
        const coordinates = e.features[0].geometry.coordinates.slice();
        const description = `<div style='text-align:center; font-size:13px'><b>${e.features[0].properties.name}</b></div>`;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        popupRef.current.setLngLat(coordinates).setHTML(description).addTo(map.current);

        const parsedProps = { ...e.features[0].properties };
        for (let key in parsedProps) {
            try {
                if (typeof parsedProps[key] === 'string' && (parsedProps[key].startsWith('{') || parsedProps[key].startsWith('['))) {
                    parsedProps[key] = JSON.parse(parsedProps[key]);
                }
            } catch (err) { }
        }
        setSelectedDevice(parsedProps);
    };

    const handleMouseLeave = () => {
        if (map.current) map.current.getCanvas().style.cursor = '';
        popupRef.current.remove();
    };

    // --- Pure Data Mutation Pipeline ---
    const updateMapData = (mapInstance) => {
        const { devices: allDevices, groups, uniqueZIndices, showAdvancedDeviceLevel } = stateRef.current;

        if (!mapInstance.getSource('groups-source') || !mapInstance.getSource('devices-source')) return;

        const groupGeoJSON = {
            "type": "FeatureCollection",
            "features": groups.flatMap((group) => setGroupFeatures(group, showAdvancedDeviceLevel))
        };
        mapInstance.getSource('groups-source').setData(groupGeoJSON);

        const devicesGeoJSON = { type: "FeatureCollection", features: allDevices };
        mapInstance.getSource('devices-source').setData(devicesGeoJSON);
    };

    // --- 3. Edit Mode Overlay Logic ---
    useEffect(() => {
        const mapInstance = map.current;
        if (!mapInstance) return;

        const { uniqueZIndices, devices: allDevices } = stateRef.current;

        // Toggle layer visibility helper
        const setGeoJsonVisibility = (visibility) => {
            uniqueZIndices.forEach(zIndex => {
                if (mapInstance.getLayer(`devices-layer-z${zIndex}`)) {
                    mapInstance.setLayoutProperty(`devices-layer-z${zIndex}`, 'visibility', visibility);
                }
                if (mapInstance.getLayer(`devices-img-layer-z${zIndex}`)) {
                    mapInstance.setLayoutProperty(`devices-img-layer-z${zIndex}`, 'visibility', visibility);
                }
            });
        };

        if (isEditMode) {
            // Hide standard layers
            setGeoJsonVisibility('none');
            setSelectedDevice(null);

            // Generate native draggable markers over the exact coordinates
            allDevices.forEach(feature => {
                const uuid = feature.properties.uuid;
                const coords = feature.geometry.coordinates;
                const name = feature.properties.name;

                // Create a standard Mapbox marker, making it visually distinct for editing
                const marker = new mapboxgl.Marker({ draggable: true, color: '#f5365c' })
                    .setLngLat(coords)
                    .setPopup(new mapboxgl.Popup({ offset: 25, closeButton: false }).setHTML(`<div style='text-align:center; font-size:13px; font-family:sans-serif;'><b>${name}</b><br/>Drag to Reposition</div>`))
                    .addTo(mapInstance);

                // Flash the popup temporarily when dragging starts to confirm selection
                marker.on('dragstart', () => {
                    marker.togglePopup();
                });

                marker.on('dragend', () => {
                    const lngLat = marker.getLngLat();
                    // 2. Call the ref directly to avoid dependency loops!
                    if (handleLocationChangeRef.current) {
                        handleLocationChangeRef.current(uuid, lngLat.lat, lngLat.lng);
                    }
                    marker.togglePopup();
                });

                editMarkersRef.current.push(marker);
            });
        } else {
            // Cleanup markers when editing finishes
            editMarkersRef.current.forEach(marker => marker.remove());
            editMarkersRef.current = [];

            // Restore standard layers
            setGeoJsonVisibility('visible');
        }

        return () => {
            // Cleanup just in case component unmounts mid-edit
            editMarkersRef.current.forEach(marker => marker.remove());
            editMarkersRef.current = [];
        };
    }, [isEditMode, map]);

    // --- The Structural Instantiation Loop ---
    useEffect(() => {
        const mapInstance = map.current;
        if (!mapInstance) return;

        let isCancelled = false;

        const buildInfrastructure = async () => {
            if (!mapInstance.isStyleLoaded() || isCancelled) return;

            // Load Custom Canvas Assets Safely
            const icons = [
                { id: 'cameraIconOnline', img: cameraOnline },
                { id: 'cameraIconWarning', img: cameraWarning },
                { id: 'cameraIconOffline', img: cameraOffline },
                { id: 'deviceIconSleeping', img: deviceSleeping },
                { id: 'deviceIconEcoMode', img: deviceEcoMode },
                { id: 'deviceIconEcoModeWarning', img: deviceEcoModeWarning }
            ];

            await Promise.all(icons.map(icon => {
                return new Promise((resolve) => {
                    if (isCancelled || !mapInstance || mapInstance.hasImage(icon.id)) return resolve();
                    mapInstance.loadImage(icon.img, (err, res) => {
                        if (!isCancelled && res && !mapInstance.hasImage(icon.id)) {
                            mapInstance.addImage(icon.id, res);
                        }
                        resolve();
                    });
                });
            }));

            if (isCancelled) return;

            const { devices: allDevices, groups, uniqueZIndices, showAdvancedDeviceLevel, mapOption } = stateRef.current;

            // Inject Groups Structure
            if (!mapInstance.getSource('groups-source')) {
                const groupGeoJSON = { "type": "FeatureCollection", "features": groups.flatMap((g) => setGroupFeatures(g, showAdvancedDeviceLevel)) };
                mapInstance.addSource('groups-source', { type: 'geojson', data: groupGeoJSON });
                mapInstance.addLayer({ id: 'groups-layer', type: 'fill', source: 'groups-source', paint: { 'fill-color': ['get', 'feature_colour'], 'fill-opacity': 0.2 } });
                mapInstance.addLayer({ id: 'groups-layer-liner', type: 'line', source: 'groups-source', paint: { "line-color": ['get', 'feature_colour'], "line-width": 2 } });
            }

            // Inject Devices Structure
            if (!mapInstance.getSource('devices-source')) {
                const devicesGeoJSON = { type: "FeatureCollection", features: allDevices };
                mapInstance.addSource('devices-source', { type: 'geojson', data: devicesGeoJSON });
            }

            const imgLayerFilter = mapOption != null && !isAttentionMode ? ['literal', false] : ['any', ['==', ['get', 'hardware_type'], 'bk-camera'], ['==', ['get', 'connected_state'], 'sleeping'], ['==', ['get', 'connected_state'], 'eco']];
            const circleLayerFilter = mapOption != null || isAttentionMode ? ['literal', true] : ['all', ['!=', ['get', 'hardware_type'], 'bk-camera'], ['!=', ['get', 'connected_state'], 'sleeping'], ['!=', ['get', 'connected_state'], 'eco']];

            uniqueZIndices.forEach((zIndex) => {
                if (!mapInstance.getLayer(`devices-layer-z${zIndex}`)) {
                    mapInstance.addLayer({
                        id: `devices-layer-z${zIndex}`,
                        type: 'circle',
                        source: 'devices-source',
                        filter: ['all', ['==', ['get', 'zIndex'], zIndex], circleLayerFilter],
                        paint: {
                            'circle-color': ['get', 'feature_colour'],
                            'circle-radius': 9,
                            'circle-opacity': ['get', 'opacity'],
                            'circle-stroke-width': 3,
                            'circle-stroke-color': '#ffffff',
                            'circle-stroke-opacity': ['get', 'opacity']
                        }
                    });
                    mapInstance.on('click', `devices-layer-z${zIndex}`, handleDeviceClick);
                    mapInstance.on('mouseenter', `devices-layer-z${zIndex}`, handleMouseEnter);
                    mapInstance.on('mouseleave', `devices-layer-z${zIndex}`, handleMouseLeave);

                    // debugger;
                }

                if (!mapInstance.getLayer(`devices-img-layer-z${zIndex}`)) {
                    mapInstance.addLayer({
                        id: `devices-img-layer-z${zIndex}`,
                        type: 'symbol',
                        source: 'devices-source',
                        filter: ['all', ['==', ['get', 'zIndex'], zIndex], imgLayerFilter],
                        paint: {
                            'icon-opacity': ['get', 'opacity']
                        },
                        layout: {
                            'icon-image': ['get', 'icon'],
                            'icon-size': 0.042,
                            'icon-allow-overlap': true,
                        }
                    });
                    mapInstance.on('click', `devices-img-layer-z${zIndex}`, handleDeviceClick);
                    mapInstance.on('mouseenter', `devices-img-layer-z${zIndex}`, handleMouseEnter);
                    mapInstance.on('mouseleave', `devices-img-layer-z${zIndex}`, handleMouseLeave);
                }
            });

            // Base Map Listeners
            mapInstance.on('click', handleMapClick);
            mapInstance.on('click', 'groups-layer', handleGroupClick);
            mapInstance.on('mouseenter', 'groups-layer', handleMouseEnter);
            mapInstance.on('mouseleave', 'groups-layer', handleMouseLeave);

            // Set Atmosphere Fog
            const isDark = user?.user?.mode.includes("dark");
            mapInstance.setFog(isDark ? { 'color': 'rgba(255,255,255,0.06)', 'high-color': 'rgba(170,190,255,0.25)', 'space-color': '#05070f', 'horizon-blend': 0.18, 'star-intensity': 0.25 } : { 'color': '#ffffff', 'high-color': '#ffffff', 'space-color': '#333333', 'horizon-blend': 0.05, 'star-intensity': 0.4 });

            // CRITICAL ENGINE FIX: Force dimension calculations immediately after elements paint
            mapInstance.resize();
        };

        // Bind setup loop conditionally based on initialization timing
        if (mapInstance.isStyleLoaded()) {
            buildInfrastructure();
        } else {
            mapInstance.once('style.load', buildInfrastructure);
        }

        // --- Bulletproof Cleanup Execution ---
        return () => {
            isCancelled = true;
            if (!mapInstance) return;

            mapInstance.off('style.load', buildInfrastructure);
            mapInstance.off('click', handleMapClick);
            mapInstance.off('click', 'groups-layer', handleGroupClick);
            mapInstance.off('mouseenter', 'groups-layer', handleMouseEnter);
            mapInstance.off('mouseleave', 'groups-layer', handleMouseLeave);

            const { uniqueZIndices } = stateRef.current;
            uniqueZIndices.forEach((zIndex) => {
                mapInstance.off('click', `devices-img-layer-z${zIndex}`, handleDeviceClick);
                mapInstance.off('mouseenter', `devices-img-layer-z${zIndex}`, handleMouseEnter);
                mapInstance.off('mouseleave', `devices-img-layer-z${zIndex}`, handleMouseLeave);
                mapInstance.off('click', `devices-layer-z${zIndex}`, handleDeviceClick);
                mapInstance.off('mouseenter', `devices-layer-z${zIndex}`, handleMouseEnter);
                mapInstance.off('mouseleave', `devices-layer-z${zIndex}`, handleMouseLeave);

                if (mapInstance.getLayer(`devices-img-layer-z${zIndex}`)) mapInstance.removeLayer(`devices-img-layer-z${zIndex}`);
                if (mapInstance.getLayer(`devices-layer-z${zIndex}`)) mapInstance.removeLayer(`devices-layer-z${zIndex}`);
            });

            if (mapInstance.getLayer('groups-layer-liner')) mapInstance.removeLayer('groups-layer-liner');
            if (mapInstance.getLayer('groups-layer')) mapInstance.removeLayer('groups-layer');
            if (mapInstance.getSource('groups-source')) mapInstance.removeSource('groups-source');
            if (mapInstance.getSource('devices-source')) mapInstance.removeSource('devices-source');
        };
    }, [map, selectedOrg, mapOption, isAttentionMode]); // Only restart structural mounts if the core map engine or org resets
}

StopmonitorMap.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    devices: PropTypes.array,
    groups: PropTypes.array,
    attentionDevices: PropTypes.array,
    setSelectedDevice: PropTypes.func,
    mapOption: PropTypes.object,
    setMapOption: PropTypes.func,
    isEditMode: PropTypes.bool,
    handleLocationChange: PropTypes.func,
};

export default StopmonitorMap;